import React, { useState, useEffect, useMemo } from 'react';
import { MTableBodyRow } from 'material-table';
import { MTable } from 'ui/mtable/MTable';
import formatters from '../../../formatters';
import { PAActionOptions } from '../Consts';
import PasswordAuditingImg from '../../../images/password-auditing-165x165.png';
import { Button, Typography, Grid } from '@material-ui/core';
import Dashboard from 'ui/dashboard/Dashboard';
import DashboardBox from 'ui/dashboard/Box';
import DashboardBoxContent from 'ui/dashboard/BoxContent';
import DashboardBoxActions from 'ui/dashboard/BoxActions';
import { loadPolices } from '../../company/policies/services';
import { useReports } from '../../hooks/useReports';

const getColumns = () => {
    return [
        {
            field: 'id',
            title: '#',
            notFilter: true,
            export: false,
        },
        {
            field: 'date',
            title: 'Date',
            defaultSort: 'desc',
            render: rowData => formatters.date(rowData.date),
        },
        {
            field: 'company',
            title: 'Company',
        },
        {
            field: 'user',
            title: 'User',
        },
        {
            field: 'account_name',
            title: 'Account name',
        },
        {
            field: 'action',
            title: 'Action',
            filterType: 'select',
            filterOptions: PAActionOptions,
        },
        {
            field: 'account_username',
            title: 'Account Username',
        },
        {
            field: 'occured_on_device',
            title: 'Occured on Device',
        },
    ];
};

const logsMapping = data => {
    return data.map((item, index) => ({
        id: index + 1,
        ...item,
    }));
};

const PasswordAudit = ({ dateRange, organization, history, app }) => {
    const { currentUser } = app;
    const [enabled, setEnabled] = useState(null);
    const [policyLoading, setPolicyLoading] = useState(true);

    // Always call the hook (hooks must be called unconditionally)
    const { data, isLoading, isValidating, refresh } = useReports(
        currentUser,
        'password_auditing',
        dateRange
    );

    // Transform data with useMemo to avoid re-computation
    const logs = useMemo(() => {
        return Array.isArray(data) ? logsMapping(data) : [];
    }, [data]);

    useEffect(() => {
        setPolicyLoading(true);
        loadPolices(organization).then(({ policies }) => {
            const policy =
                policies.find(
                    policy => policy.policy.identifier === 'POLICY-017'
                ) || {};
            setEnabled(policy.enabled || false);
            setPolicyLoading(false);
        });
    }, [organization]);

    return (
        <>
            {enabled && (
                <MTable
                    stateKey="PasswordAuditing"
                    columns={getColumns()}
                    data={logs}
                    loading={policyLoading || isLoading || isValidating}
                    onRefresh={refresh}
                    title={null}
                    components={{
                        Row: props => {
                            props.data.id = props.index + 1;
                            return <MTableBodyRow {...props} />;
                        },
                    }}
                />
            )}
            {!enabled && (
                <Dashboard width="70" align="center">
                    <DashboardBox>
                        <DashboardBoxContent>
                            <Grid container spacing={3} direction="row">
                                <Grid item xs="auto">
                                    <img
                                        src={PasswordAuditingImg}
                                        alt="password auditing"
                                    />
                                </Grid>
                                <Grid item xs>
                                    <Grid
                                        container
                                        justifyContent="center"
                                        direction="column"
                                        style={{ height: '100%' }}
                                    >
                                        <Typography variant="h5" gutterBottom>
                                            Password auditing allows you to
                                            track access and usage of passwords
                                        </Typography>
                                        <Typography
                                            color="textSecondary"
                                            variant="body1"
                                            gutterBottom
                                            style={{ marginBottom: 24 }}
                                        >
                                            Password Auditing is configured
                                            within Security Policies on the
                                            Policies tab. Once password auditing
                                            is enabled you will see activity on
                                            this tab.
                                        </Typography>
                                        <Typography
                                            color="textSecondary"
                                            variant="subtitle2"
                                            gutterBottom
                                        >
                                            Password auditing is avaliable to
                                            companies with an Advanced or Trial
                                            subscription.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </DashboardBoxContent>
                        <DashboardBoxActions>
                            <Button
                                color="secondary"
                                onClick={() =>
                                    history.push(
                                        `/companies/${organization}/policies`
                                    )
                                }
                            >
                                ENABLE PASSWORD AUDITING
                            </Button>
                        </DashboardBoxActions>
                    </DashboardBox>
                </Dashboard>
            )}
        </>
    );
};

export { PasswordAudit };
